/**
 * @author Rahul Nainala
 * @description This is a custom 3d Renderer designed for modelsConfig
 */
import "./index.css";
import { message } from 'antd';
import { connect } from "react-redux";
import { Canvas } from "@react-three/fiber";
import Points from "../re-components/Points";
import { useParams } from 'react-router-dom';
import Loader from "../re-components/Loader";
import InfoCard from "../re-components/InfoCard";
import { ModelLoader } from "../re-components/ModelLoader";
import { Environment, OrbitControls } from "@react-three/drei";
import React, { useState, useEffect, useRef, Suspense } from "react";
import { configurationData, MastersKeyData, ModelData } from '../API';

const App = (props) => {
    let params = useParams();
    let payload = params;
    let intervalRef = useRef(null);
    let selectedObjectNameGlobal = null;
    let [keyData, setKeyData] = useState([]);
    let [urlLink, setUrlLink] = useState(null);
    let [showUrl, setShowUrl] = useState(true);
    let [frequency, setFrequency] = useState("");
    let [cardOpen, setCardOpen] = useState(false);
    let [annotations, setAnnotations] = useState([]);
    let [description, setDescription] = useState("");
    let [colorHelper, setColorHelper] = useState("");
    let [selectedMesh, setSelectedMesh] = useState(null);
    let [displayTitle, setDisplayTitle] = useState(null);
    let [visualHelpers, setVisualHelpers] = useState("");
    let [selectedObjectName, setSelectedObjectName] = useState(null);
    let [selectedModalConfig, setSelectedModalConfig] = useState([]);
    let [translationCache, setTranslationCache] = useState(props.translationCache || []);

    useEffect(() => {
        if (translationCache !== props.translationCache) {
            setTranslationCache(props.translationCache);
        }
    }, [props.translationCache]);

    const translation = (keyword) => {
        return translationCache && translationCache[keyword] ? translationCache[keyword] : keyword;
    };

    useEffect(() => {
        async function fetchData() {
            try {
                let response = await configurationData(payload);
                setFrequency(response[0].JSON.child.frequency);
                setAnnotations(response[0].JSON.child.annotations);
                setColorHelper(response[0].JSON.child.colorHelper);
                setVisualHelpers(response[0].JSON.child.coordinates);
            } catch (e) {
                console.error("Facing error while loading", e);
            }
        }
        fetchData();
    }, []);

    useEffect(() => {
        async function fetchData() {
            try {
                let response = await MastersKeyData(payload);
                setKeyData(response);
            } catch (e) {
                console.error("Facing error while loading Key", e);
            }
        }
        fetchData();
    }, []);

    let convertToMilliseconds = (interval) => {
        try {
            let value = parseInt(interval.slice(0, -1));
            let unit = interval.slice(-1).trim();

            const conversionFactors = {
                "s": 1000, "S": 1000,
                "m": 60000, "M": 60000,
                "h": 3600000, "H": 3600000
            };

            if (unit in conversionFactors) {
                return value * conversionFactors[unit];
            } else {
                message.error(translation("Invalid interval format. Use 's', 'm', or 'h'."));
                return null;
            }
        } catch (e) {
            console.error("Facing an error while converting to Ms", e);
        }
    };

    let getModelData = async (props) => {
        try {
            let data = await ModelData(props);
            return data;
        } catch (e) {
            console.error("Facing an error while loading the Model", e);
        }
    };

    let handleCloseCard = () => {
        try {
            setCardOpen(false);
            if (selectedMesh) {
                selectedMesh.material.color.set("#ffffff");
            }
            setSelectedMesh(null);
            setSelectedObjectName(null);
            setSelectedModalConfig([]);
            setDescription("");
            clearInterval(intervalRef.current);
        } catch (e) {
            console.error("Facing and error while closing card", e);
        }
    };

    useEffect(() => {
        async function fetchData() {
            try {
                if (selectedObjectName) {
                    const modelConfig = annotations.find(config => {
                        if (!config.clickable) return false;
                        return Object.keys(config).some(key => {
                            for (let value of keyData.map((item) => item.Key)) {
                                if (key === value) {
                                    return Array.isArray(config[key]) && config[key].some(item => item.id === selectedObjectName);
                                }
                            }
                            return false;
                        });
                    });
                    if (modelConfig) {
                        let foundModalConfig = null;
                        let categoryType = null;
                        for (const key of Object.keys(modelConfig)) {
                            for (let value of keyData.map((item) => item.Key)) {
                                if (key === value) {
                                    if (Array.isArray(modelConfig[key])) {
                                        const item = modelConfig[key].find(item => item.id === selectedObjectName);
                                        if (item) {
                                            categoryType = key;
                                            setDescription(item.description);
                                            setDisplayTitle(item.parentDisplayName);
                                            setUrlLink(item.url);
                                            setShowUrl(item.showUrl);
                                            if (item.showModal && item.showModal.length > 0) {
                                                foundModalConfig = item.showModal[0];
                                                break;
                                            }
                                        }
                                    }
                                }
                            }
                        }

                        if (foundModalConfig) {
                            const intervalInMs = convertToMilliseconds(frequency);
                            let payload = {
                                "Name": params.Name,
                                "Parent_Name": modelConfig.name,
                                "Id": selectedObjectName,
                                "Type": categoryType
                            };
                            const fetchModelData = async () => {
                                try {
                                    let response = await getModelData(payload);
                                    if (response && response.length > 0) {
                                        setSelectedModalConfig(response);
                                        setCardOpen(true);
                                    } else {
                                        setSelectedModalConfig([]);
                                        setCardOpen(false);
                                    }
                                } catch (error) {
                                    setSelectedModalConfig([]);
                                    setCardOpen(false);
                                }
                            };

                            fetchModelData();
                            if (intervalInMs) {
                                intervalRef.current = setInterval(fetchModelData, intervalInMs);
                            }

                        } else {
                            message.error("No configuration found for:" + selectedObjectName);
                            setSelectedModalConfig([]);
                        }
                    } else {
                        setSelectedModalConfig([]);
                    }
                } else {
                    clearInterval(intervalRef.current);
                }
            } catch (e) {
                console.error("Facing error while loading Model", e);
            }
        }
        fetchData();
        return () => {
            clearInterval(intervalRef.current);
        };
    }, [selectedObjectName, frequency, params.Name, annotations]);

    return (
        <>
            <div style={{ width: "100vw", height: "90vh", position: "relative" }}>
                {cardOpen && selectedObjectName && selectedModalConfig && (
                    <InfoCard
                        cardOpen={cardOpen}
                        selectedObjectName={selectedObjectName}
                        selectedModalConfig={selectedModalConfig}
                        displayTitle={displayTitle}
                        urlLink={urlLink}
                        showUrl={showUrl}
                        description={description}
                        onClose={handleCloseCard}
                    />
                )}
                <div style={{ display: "flex", alignItems: 'center', justifyContent: 'center', flexDirection: "row" }}>
                    <Canvas style={{ height: '90vh', width: '98vw', border: "1px solid #ffff" }} camera={{ position: [10, 10, 10] }}>
                        <Suspense fallback={<Loader />}>
                            <ambientLight intensity={1.5} />
                            <directionalLight position={[40, 5, 10]} intensity={2.1} />
                            <OrbitControls
                                makeDefault
                            />
                            <Environment files="http://20.198.16.173/Models/hdr/snow.hdr" />
                            <ModelLoader
                                selectedObjectNameGlobal={selectedObjectNameGlobal}
                                colorHelper={colorHelper}
                                config={annotations}
                                selectedMesh={selectedMesh}
                                setSelectedMesh={(mesh) => {
                                    setSelectedMesh(mesh);
                                    setSelectedObjectName(mesh ? mesh.name : null);
                                }}
                            />
                            {visualHelpers === 'true' ?
                                <>
                                    <axesHelper args={[5]} />
                                    <Points />
                                </> : null}
                        </Suspense>
                    </Canvas>
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => ({
    translationCache: state.TranslationReducer.translationCache
});
export default connect(mapStateToProps)(App);
